import React, { MouseEvent, useState } from 'react';
import { useLocale } from 'client/i18n/hooks/useLocale';
import { LocalesInfo } from 'client/i18n/types';
import i18nLocales from 'client/i18n/locales.json';
import { ChevronDownIcon } from 'shared/Icon/components/ChevronDownIcon';
import { GlobeIcon } from '../svgs/GlobeIcon';
import {
  StyledLanguageSelectorContainer,
  StyledLanguageSelectorDisplay,
  StyledLanguageSelectorList,
  StyledLanguageSelectorListContainer,
  StyledLanguageSelectorListItem,
} from './styles';

type I18NLanguageProp = {
  label: string;
  value: string;
};

const pseudoOption = {
  label: 'Pseudo',
  value: 'pseudo',
} as I18NLanguageProp;

const localesOptions = Object.entries(i18nLocales as LocalesInfo).reduce<
  I18NLanguageProp[]
>(
  (acc, [locale, { displayName }]) =>
    acc.concat([{ label: displayName, value: locale }]),
  []
);

// TODO: https://anchorfm.atlassian.net/browse/CAST-2581: Remove all isInNav vs PSC flow logic when we remove the
// language selector from the PSC flow and solely use the navbar langauge selector for the full site.
export function I18NLanguageSelector({
  isInNav,
  isDesktop,
}: {
  isInNav?: boolean;
  isDesktop?: boolean;
}) {
  const { changeLocale, enableI18N, locale } = useLocale();
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const handleClick = (event: MouseEvent<HTMLLIElement>) => {
    const newLocale =
      (event.target as HTMLInputElement).getAttribute('value') || '';
    // TODO https://anchorfm.atlassian.net/browse/WHEEL-807: Find Solution Without Utilizing GA Global Object
    if (window.ga !== undefined) {
      const gaEventParams = {
        eventCategory: 'Profile Page Subscribe',
        eventAction: 'Language Selector',
        eventLabel: newLocale,
      };

      // TODO https://anchorfm.atlassian.net/browse/WHEEL-807: Find Solution Without Utilizing GA Global Object
      window.ga('send', 'event', gaEventParams);
    }

    changeLocale(newLocale);
  };

  const globeAriaLabel = `languageSelector${isInNav ? 'Nav' : ''}${
    isDesktop ? 'Desktop' : ''
  }`;

  const currentLocaleOptions = enableI18N
    ? [...localesOptions, pseudoOption]
    : localesOptions;

  return (
    <StyledLanguageSelectorContainer
      data-cy={`language-selector__${isInNav ? 'nav' : 'psc'}`}
      isInNav={!!isInNav}
      onBlur={() => (isOpen ? setIsOpen(false) : null)}
      onClick={() => setIsOpen(!isOpen)}
      tabIndex={0}
    >
      <StyledLanguageSelectorDisplay
        data-cy={`language-selector__display__${isInNav ? 'nav' : 'psc'}`}
        isInNav={!!isInNav}
      >
        <GlobeIcon additionalAriaId={globeAriaLabel} />
        <span>{locale}</span>
        <ChevronDownIcon />
      </StyledLanguageSelectorDisplay>
      {isOpen && currentLocaleOptions.length && (
        <StyledLanguageSelectorListContainer>
          <StyledLanguageSelectorList isInNav={!!isInNav}>
            {currentLocaleOptions.map((option: I18NLanguageProp) => (
              <StyledLanguageSelectorListItem
                isSelected={locale === option.value}
                key={option.label}
                onClick={handleClick}
                value={option.value}
              >
                {option.label}
              </StyledLanguageSelectorListItem>
            ))}
          </StyledLanguageSelectorList>
        </StyledLanguageSelectorListContainer>
      )}
    </StyledLanguageSelectorContainer>
  );
}
